import React, { useState, useEffect, useRef } from 'react';
import './ServiceModal.css';
import Modal from './common/Modal';
import { ServiceType, ServiceTypeNames, ServiceTypeOptions } from '../constants/serviceTypes';
import apiClient from '../api';

const ServiceModal = ({ isOpen, onClose, onSave, service }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 0,
    tariffs: []
  });
  const [allTariffs, setAllTariffs] = useState([]);
  const [filteredTariffs, setFilteredTariffs] = useState([]);
  const [showTariffDropdown, setShowTariffDropdown] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState('up');
  const tariffSelectorRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchTariffs();
      if (service) {
        setFormData({
          name: service.name || '',
          description: service.description || '',
          type: service.type !== undefined ? service.type : 0,
          tariffs: service.tariffs || service.service_tariffs || []
        });
      } else {
        setFormData({
          name: '',
          description: '',
          type: 0,
          tariffs: []
        });
      }
    }
  }, [service, isOpen]);

  const fetchTariffs = async () => {
    try {
      const response = await apiClient.get('/tariffs');
      setAllTariffs(response.data);
    } catch (error) {
      console.error('Ошибка загрузки тарифов:', error);
    }
  };

  useEffect(() => {
    // Получаем ID уже добавленных тарифов
    const addedTariffIds = formData.tariffs.map(t => {
      return t.id !== undefined ? t.id : t.tariff_id;
    });
    
    // Фильтруем по типу и исключаем уже добавленные
    const filtered = allTariffs.filter(tariff => 
      tariff.type === formData.type && !addedTariffIds.includes(tariff.id)
    );
    setFilteredTariffs(filtered);
  }, [formData.type, allTariffs, formData.tariffs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTypeChange = (e) => {
    const typeValue = parseInt(e.target.value);
    const typeName = ServiceTypeNames[typeValue];
    
    // Если у услуги уже были добавлены тарифы и мы меняем тип, очищаем их
    if (formData.tariffs.length > 0 && formData.type !== typeValue) {
      const confirmClear = window.confirm(
        'При изменении типа услуги все добавленные тарифы будут удалены. Продолжить?'
      );
      if (!confirmClear) {
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        type: typeValue,
        name: !prev.name || Object.values(ServiceTypeNames).includes(prev.name) ? typeName : prev.name,
        tariffs: []
      }));
    } else {
      // Если тарифов нет или это первая установка, просто меняем тип
      setFormData(prev => ({
        ...prev,
        type: typeValue,
        name: !prev.name || Object.values(ServiceTypeNames).includes(prev.name) ? typeName : prev.name
      }));
    }
    setShowTariffDropdown(false);
  };

  const handleAddTariff = (tariff) => {
    const tariffExists = formData.tariffs.some(t => {
      const tId = t.id !== undefined ? t.id : t.tariff_id;
      return tId === tariff.id;
    });
    
    if (!tariffExists) {
      setFormData(prev => ({
        ...prev,
        tariffs: [...prev.tariffs, {
          id: tariff.id,
          tariff_id: tariff.id,
          name: tariff.name,
          type: tariff.type,
          value: tariff.value,
          coefficient: tariff.coefficient
        }]
      }));
    }
    setShowTariffDropdown(false);
  };

  const handleRemoveTariff = (tariffId) => {
    setFormData(prev => ({
      ...prev,
      tariffs: prev.tariffs.filter(t => {
        const tId = t.id !== undefined ? t.id : t.tariff_id;
        return tId !== tariffId;
      })
    }));
  };

  // Закрытие меню при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tariffSelectorRef.current && !tariffSelectorRef.current.contains(event.target)) {
        setShowTariffDropdown(false);
      }
    };

    if (showTariffDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showTariffDropdown]);

  // Определяем направление открытия меню
  const handleDropdownOpen = () => {
    if (!showTariffDropdown) {
      setShowTariffDropdown(true);
      // Используем setTimeout чтобы убедиться, что DOM обновлен
      setTimeout(() => {
        if (tariffSelectorRef.current) {
          const rect = tariffSelectorRef.current.getBoundingClientRect();
          const dropdownHeight = 300; // max-height из CSS
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          // Если места снизу достаточно или места сверху недостаточно, открываем вниз
          if (spaceBelow >= dropdownHeight || spaceAbove < dropdownHeight) {
            setDropdownDirection('down');
          } else {
            setDropdownDirection('up');
          }
        }
      }, 0);
    } else {
      setShowTariffDropdown(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} overlayClassName="modal-overlay" contentClassName="modal-content">
      <div className="modal-header">
        <h2>{service ? 'Редактировать услугу' : 'Новая услуга'}</h2>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Название *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Электричество"
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">Описание</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Счетчик электроэнергии"
            rows="4"
          />
        </div>
        <div className="form-group">
          <label htmlFor="type">Тип услуги *</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleTypeChange}
            required
          >
            {ServiceTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Тарифы</label>
          <div className="tariff-selector" ref={tariffSelectorRef}>
            <button
              type="button"
              className="button-secondary"
              onClick={handleDropdownOpen}
            >
              + Добавить тариф
            </button>
            {showTariffDropdown && (
              <div className={`tariff-dropdown ${dropdownDirection === 'down' ? 'open-down' : ''}`}>
                {filteredTariffs.length > 0 ? (
                  <ul className="tariff-list">
                    {filteredTariffs.map(tariff => (
                      <li key={tariff.id}>
                        <button
                          type="button"
                          onClick={() => handleAddTariff(tariff)}
                          className="tariff-option"
                        >
                          <div className="tariff-option-name">{tariff.name}</div>
                          <div className="tariff-option-value">
                            {Number(tariff.value).toFixed(2)} руб.
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="no-tariffs">
                    Нет доступных тарифов для этого типа услуги
                  </div>
                )}
              </div>
            )}
          </div>

          {formData.tariffs.length > 0 && (
            <div className="selected-tariffs">
              <h4>Выбранные тарифы:</h4>
              <div className="tariff-table">
                <div className="tariff-row tariff-header">
                  <div>Название</div>
                  <div>Стоимость</div>
                  <div>Коэффициент</div>
                  <div></div>
                </div>
                {formData.tariffs.map(tariff => (
                  <div key={tariff.id || tariff.tariff_id} className="tariff-row">
                    <div>{tariff.name}</div>
                    <div>{Number(tariff.value).toFixed(2)}</div>
                    <div>{tariff.coefficient ?? 1}</div>
                    <div>
                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => handleRemoveTariff(tariff.id || tariff.tariff_id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="button-primary">
            {service ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ServiceModal;
